import { Fragment, useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import styles from './index-switcher.module.scss';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';
import { Image } from '@utmb/design-system/image/Image';
import { getCategoryColor } from '@utmb/utils/colors';
import type { CSSProperties } from 'react';

export interface IndexSwitcherProps {
    value?: PiCategoryEnum;
    onChange?: (value: PiCategoryEnum) => void;
    isDark?: boolean;
}

const CATEGORIES: PiCategoryEnum[] = [PiCategoryEnum.PI20K, PiCategoryEnum.PI50K, PiCategoryEnum.PI100K, PiCategoryEnum.PI100M];

export function IndexSwitcher({ value, onChange, isDark }: IndexSwitcherProps): ReactElement {
    const [internalSelection, setInternalSelection] = useState<PiCategoryEnum>(value ?? PiCategoryEnum.GENERAL);
    const selection = value !== undefined ? value : internalSelection;

    const containerRef = useRef<HTMLDivElement>(null);
    const entryRefs = useRef<Map<PiCategoryEnum, HTMLButtonElement>>(new Map());
    const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number; ready: boolean }>({
        left: 0,
        width: 0,
        ready: false,
    });

    function handleClick(next: PiCategoryEnum) {
        if (value === undefined) {
            setInternalSelection(next);
        }
        onChange?.(next);
    }

    useLayoutEffect(() => {
        function updateIndicator() {
            const container = containerRef.current;
            const target = entryRefs.current.get(selection ?? PiCategoryEnum.GENERAL);
            if (!container || !target) return;
            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            setIndicatorStyle({
                left: targetRect.left - containerRect.left,
                width: targetRect.width,
                ready: true,
            });
        }

        updateIndicator();
        window.addEventListener('resize', updateIndicator);
        return () => window.removeEventListener('resize', updateIndicator);
    }, [selection]);

    function setEntryRef(key: PiCategoryEnum) {
        return (node: HTMLButtonElement | null) => {
            if (node) {
                entryRefs.current.set(key, node);
            } else {
                entryRefs.current.delete(key);
            }
        };
    }

    return (
        <div ref={containerRef} className={styles.indexSwitcher + (isDark ? ` ${styles.darkIndexSwitcher}` : '')}>
            <span
                className={styles.indicator}
                style={{
                    transform: `translateX(${indicatorStyle.left}px)`,
                    width: `${indicatorStyle.width}px`,
                    opacity: indicatorStyle.ready ? 1 : 0,
                }}
                aria-hidden="true"
            />
            <button
                ref={setEntryRef(PiCategoryEnum.GENERAL)}
                type="button"
                className={styles.entry}
                onClick={() => handleClick(PiCategoryEnum.GENERAL)}
                aria-pressed={selection === PiCategoryEnum.GENERAL}
                style={{ ['--glow-color' as keyof CSSProperties]: 'var(--utmb-color-ws-secondary)' } as CSSProperties}
            >
                <div className={styles.mainLogo}>
                    <Image
                        objectFit="contain"
                        layout="fixed"
                        width={120}
                        height={30}
                        maxWidth={300}
                        image={pIdToCloudinary('/Common/categories/index_bg', 'UTMB Index Race')}
                        addOptions={[CloudinaryTransformEnum.TRIM]}
                    />
                </div>
            </button>
            {CATEGORIES.map((category) => {
                const isSelected = selection === category;
                return (
                    <Fragment key={category}>
                        <div className={styles.divider} />
                        <button
                            ref={setEntryRef(category)}
                            type="button"
                            className={styles.entry}
                            onClick={() => handleClick(category)}
                            aria-pressed={isSelected}
                            style={{ ['--glow-color' as keyof CSSProperties]: getCategoryColor(category) } as CSSProperties}
                        >
                            <PiCategoryLogo height={20} piCategory={category} isDark={true} />
                        </button>
                    </Fragment>
                );
            })}
        </div>
    );
}
